// Painel de conferência visual: scan + fantasma do gabarito.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { apiClient } from '../../api/apiClient';
import {
  loadExamObjectiveOverlayPrefs,
  saveExamObjectiveOverlayPrefs,
  OVERLAY_DEFAULTS,
} from '../../utils/examObjectiveOverlayPrefs';
import { mapPreviewBubbleCoords } from '../../utils/examObjectiveOverlayMath';

const OVERLAY_COLOR = '#DC2626';
const WILDCARD_COLOR = '#7C3AED';

export const ExamObjectiveOverlayPanel = React.memo(
  ({
    applicationId,
    processId,
    onPrefsChange,
    variant = 'default',
    showScaleX = false,
    persistLocalPrefs = true,
    controlledPrefs = null,
    onControlledPrefsChange = null,
    useProcessCalibration = true,
    calibrationProcessId = null,
    externalReview = null,
    previewVersion = 0,
    previewStaging = false,
  }) => {
    const isCalibration = Boolean(calibrationProcessId);
    const isLarge = variant === 'large';
    const imageViewportMaxHeight = isLarge ? 'calc(90vh - 260px)' : '58vh';
    const isControlled = controlledPrefs != null && onControlledPrefsChange != null;

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [review, setReview] = useState(null);
    const [localOpacity, setLocalOpacity] = useState(OVERLAY_DEFAULTS.opacity);
    const [localOffsetX, setLocalOffsetX] = useState(OVERLAY_DEFAULTS.offsetX);
    const [localOffsetY, setLocalOffsetY] = useState(OVERLAY_DEFAULTS.offsetY);
    const [localScale, setLocalScale] = useState(OVERLAY_DEFAULTS.scale);
    const [localScaleX, setLocalScaleX] = useState(100);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

    const opacity = isControlled ? controlledPrefs.opacity ?? OVERLAY_DEFAULTS.opacity : localOpacity;
    const offsetX = isControlled ? controlledPrefs.offsetX ?? 0 : localOffsetX;
    const offsetY = isControlled ? controlledPrefs.offsetY ?? 0 : localOffsetY;
    const scale = isControlled ? controlledPrefs.scale ?? 100 : localScale;
    const scaleX = isControlled ? controlledPrefs.scaleX ?? 100 : localScaleX;

    const setOpacity = (v) =>
      isControlled
        ? onControlledPrefsChange({ ...controlledPrefs, opacity: v })
        : setLocalOpacity(v);
    const setOffsetX = (v) =>
      isControlled
        ? onControlledPrefsChange({ ...controlledPrefs, offsetX: v })
        : setLocalOffsetX(v);
    const setOffsetY = (v) =>
      isControlled
        ? onControlledPrefsChange({ ...controlledPrefs, offsetY: v })
        : setLocalOffsetY(v);
    const setScale = (v) =>
      isControlled ? onControlledPrefsChange({ ...controlledPrefs, scale: v }) : setLocalScale(v);
    const setScaleX = (v) =>
      isControlled ? onControlledPrefsChange({ ...controlledPrefs, scaleX: v }) : setLocalScaleX(v);

    const applyPrefs = useCallback(
      (p) => {
        if (isControlled) {
          onControlledPrefsChange({ ...OVERLAY_DEFAULTS, scaleX: 100, ...p });
        } else {
          setLocalOpacity(p.opacity ?? OVERLAY_DEFAULTS.opacity);
          setLocalOffsetX(p.offsetX ?? 0);
          setLocalOffsetY(p.offsetY ?? 0);
          setLocalScale(p.scale ?? 100);
          setLocalScaleX(p.scaleX ?? 100);
        }
      },
      [isControlled, onControlledPrefsChange]
    );

    useEffect(() => {
      if (isCalibration || !applicationId || isControlled) return;
      const saved = loadExamObjectiveOverlayPrefs(processId);
      applyPrefs(saved);
    }, [applicationId, processId, isControlled, applyPrefs, isCalibration]);

    useEffect(() => {
      if (externalReview) {
        setReview(externalReview);
        setLoading(false);
        setLoadError('');
        return;
      }
      if (isCalibration && calibrationProcessId) {
        let cancelled = false;
        (async () => {
          setLoading(true);
          setLoadError('');
          const { data, error } = await apiClient.getProcessExamOmrCalibrationReview(calibrationProcessId);
          if (cancelled) return;
          if (error) {
            setLoadError(error);
            setReview(null);
          } else {
            setReview(data);
          }
          setLoading(false);
        })();
        return () => {
          cancelled = true;
        };
      }
      if (!applicationId) return;
      let cancelled = false;
      (async () => {
        setLoading(true);
        setLoadError('');
        const { data, error } = await apiClient.getExamObjectiveReview(applicationId);
        if (cancelled) return;
        if (error) {
          setLoadError(error);
          setReview(null);
        } else {
          setReview(data);
          if (!isControlled && useProcessCalibration && data?.process_omr_calibration_saved?.active) {
            const s = data.process_omr_calibration_saved;
            applyPrefs({
              opacity: OVERLAY_DEFAULTS.opacity,
              offsetX: s.offsetX ?? 0,
              offsetY: s.offsetY ?? 0,
              scale: s.scale ?? 100,
              scaleX: s.scaleX ?? 100,
            });
          }
        }
        setLoading(false);
      })();
      return () => {
        cancelled = true;
      };
    }, [
      applicationId,
      calibrationProcessId,
      externalReview,
      isCalibration,
      isControlled,
      useProcessCalibration,
      applyPrefs,
    ]);

    const persistPrefs = useCallback(
      (next) => {
        if (persistLocalPrefs && !isControlled) {
          saveExamObjectiveOverlayPrefs(processId, next);
        }
        onPrefsChange?.(next);
      },
      [processId, onPrefsChange, persistLocalPrefs, isControlled]
    );

    useEffect(() => {
      if (isCalibration || !applicationId || loading || isControlled || !persistLocalPrefs) return;
      persistPrefs({ opacity, offsetX, offsetY, scale, scaleX });
    }, [
      opacity,
      offsetX,
      offsetY,
      scale,
      scaleX,
      applicationId,
      loading,
      persistPrefs,
      isControlled,
      persistLocalPrefs,
      isCalibration,
    ]);

    const previewUrl = isCalibration
      ? `/api/processes/${calibrationProcessId}/exam_omr_calibration/preview?${previewStaging ? 'staging=1&' : ''}_=${previewVersion || 0}`
      : applicationId
        ? `/api/applications/${applicationId}/exam_objective_review/preview`
        : null;

    const geomPrefs = useMemo(
      () => ({ offsetX, offsetY, scale, scaleX }),
      [offsetX, offsetY, scale, scaleX]
    );

    const handleImageLoad = useCallback((e) => {
      setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    }, []);

    const resetPrefs = useCallback(() => {
      applyPrefs({ ...OVERLAY_DEFAULTS, scaleX: 100 });
    }, [applyPrefs]);

    if (loading) {
      return <p style={{ fontSize: 13, color: '#64748B' }}>Carregando folha e gabarito…</p>;
    }
    if (loadError) {
      return (
        <p style={{ color: '#B91C1C', fontSize: 13, padding: 12, background: '#FEF2F2', borderRadius: 8 }}>
          {loadError}
        </p>
      );
    }

    return (
      <div>
        <p style={{ fontSize: 12, color: '#475569', margin: '0 0 10px', lineHeight: 1.45 }}>
          Fundo = scan <strong>bruto do PDF</strong> (sem distorção). O fantasma aplica calibração e
          depois alinha pelas marcas de canto — <strong>mesma lógica do OMR</strong>.{' '}
          <strong style={{ color: OVERLAY_COLOR }}>Vermelho</strong> = gabarito oficial.{' '}
          <strong style={{ color: WILDCARD_COLOR }}>Roxo tracejado</strong> = questão com *.
          {persistLocalPrefs && !isControlled
            ? ' Ajuste fino só se necessário; calibração salva vale para o lote.'
            : ''}
        </p>

        {review?.omr_meta?.mark_mapping === 'bubble_grid' && (
          <p style={{ fontSize: 12, color: '#166534', marginBottom: 8 }}>
            Alinhamento pela grade de bolhas (folha modelo)
            {review.omr_meta.alignment_rmse != null
              ? ` · precisão ~${(review.omr_meta.alignment_rmse * 1000).toFixed(1)}‰`
              : ''}
          </p>
        )}

        {review?.omr_meta?.aligned_by_marks === false && review?.omr_meta?.mark_mapping !== 'bubble_grid' && (
          <p style={{ fontSize: 12, color: '#B45309', marginBottom: 8 }}>
            Marcas de canto não detectadas — alinhe com os controles abaixo.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginBottom: 10,
            fontSize: 11,
            color: '#64748B',
            alignItems: 'center',
          }}
        >
          <label>
            Opacidade
            <input
              type="range"
              min="5"
              max="100"
              value={Math.round(opacity * 100)}
              onChange={(e) => setOpacity(Number(e.target.value) / 100)}
              style={{ width: 90, marginLeft: 6, verticalAlign: 'middle' }}
            />
          </label>
          <label>
            Horizontal
            <input
              type="range"
              min="-150"
              max="150"
              value={offsetX}
              onChange={(e) => setOffsetX(Number(e.target.value))}
              style={{ width: 120, marginLeft: 6, verticalAlign: 'middle' }}
            />
            <span style={{ marginLeft: 4 }}>{offsetX}px</span>
          </label>
          <label>
            Vertical
            <input
              type="range"
              min="-150"
              max="150"
              value={offsetY}
              onChange={(e) => setOffsetY(Number(e.target.value))}
              style={{ width: 120, marginLeft: 6, verticalAlign: 'middle' }}
            />
            <span style={{ marginLeft: 4 }}>{offsetY}px</span>
          </label>
          <label>
            Escala
            <input
              type="range"
              min="88"
              max="112"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              style={{ width: 100, marginLeft: 6, verticalAlign: 'middle' }}
            />
            <span style={{ marginLeft: 4 }}>{scale}%</span>
          </label>
          {(showScaleX || scaleX !== 100) && (
            <label>
              Escala colunas
              <input
                type="range"
                min="92"
                max="108"
                value={scaleX}
                onChange={(e) => setScaleX(Number(e.target.value))}
                style={{ width: 100, marginLeft: 6, verticalAlign: 'middle' }}
              />
              <span style={{ marginLeft: 4 }}>{scaleX}%</span>
            </label>
          )}
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 11, padding: '4px 10px' }}
            onClick={resetPrefs}
          >
            Padrão fábrica
          </button>
        </div>

        <div
          style={{
            position: 'relative',
            maxHeight: imageViewportMaxHeight,
            minHeight: isLarge ? 'min(520px, calc(90vh - 260px))' : undefined,
            overflow: 'auto',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            background: '#F8FAFC',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0, width: '100%' }}>
            <img
              src={previewUrl}
              alt="Folha escaneada"
              onLoad={handleImageLoad}
              style={{ display: 'block', width: '100%', height: 'auto' }}
            />
            {imgSize.w > 0 && (
              <svg
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  opacity,
                }}
                viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                preserveAspectRatio="none"
              >
                {(review?.highlights || []).map((h) => {
                  const { cx, cy, r } = mapPreviewBubbleCoords(
                    h.x_norm,
                    h.y_norm,
                    h.r_norm,
                    imgSize.w,
                    imgSize.h,
                    geomPrefs,
                    review?.preview_homography || review?.omr_meta?.preview_homography
                  );
                  const stroke = h.wildcard ? WILDCARD_COLOR : OVERLAY_COLOR;
                  return (
                    <circle
                      key={`${h.qnum}-${h.letter}`}
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={Math.max(2, r * 0.35)}
                      strokeDasharray={h.wildcard ? '4 3' : undefined}
                    />
                  );
                })}
              </svg>
            )}
          </div>
        </div>
        {review && (
          <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
            Variante {review.variant} · {review.num_questions} questões
            {review.process_omr_calibration_saved?.active ? ' · calibração do processo ativa' : ''}
          </p>
        )}
      </div>
    );
  }
);

ExamObjectiveOverlayPanel.displayName = 'ExamObjectiveOverlayPanel';
